import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchComparisonMatrix, documentRef, type ComparisonMatrix } from '../../api/comparisonApi';
import { searchRegulations, type SearchResult } from '../../api/searchApi';
import { listDocuments, type GRCDocument } from '../../api/grcDocumentApi';
import styles from './MatrixPanel.module.css';

/**
 * Requirement matrix across 2–5 sides (SRS #169) — regulations or the
 * caller's own documents (SRS #44) — with computed coverage per cell and
 * model-written gaps and recommendations (SRS #45).
 */

interface Props {
  token: string;
}

type Side = { ref: string; label: string; kind: 'regulation' | 'document' };

const COVERAGE_LABEL = { covered: 'Covered', partial: 'Partial', absent: 'Not addressed' } as const;

const MatrixPanel: React.FC<Props> = ({ token }) => {
  const [sides, setSides] = useState<Side[]>([]);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<SearchResult[]>([]);
  const [documents, setDocuments] = useState<GRCDocument[]>([]);
  const [searching, setSearching] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ComparisonMatrix | null>(null);
  const [openCell, setOpenCell] = useState<string | null>(null);

  useEffect(() => {
    listDocuments().then(setDocuments).catch(() => setDocuments([]));
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) { setCandidates([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      searchRegulations({ query: q, size: 10 })
        .then((r) => { if (!cancelled) setCandidates(r.results); })
        .catch(() => { if (!cancelled) setCandidates([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, token]);

  const addSide = (side: Side) => {
    setError(null);
    if (sides.some((s) => s.ref === side.ref)) return;
    if (sides.length >= 5) { setError('A matrix compares at most five sides.'); return; }
    setSides([...sides, side]);
    setQuery('');
    setCandidates([]);
  };

  const run = async () => {
    if (sides.length < 2) { setError('Add at least two sides.'); return; }
    setRunning(true); setError(null); setResult(null);
    try {
      setResult(await fetchComparisonMatrix(sides.map((s) => s.ref), token));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The matrix could not be generated.');
    } finally {
      setRunning(false);
    }
  };

  const sideTitle = (i: number) => result?.sides[i]?.title || sides[i]?.label || `Side ${i + 1}`;
  const rowsById = useMemo(() => new Map((result?.rows || []).map((r) => [r.category_id, r])), [result]);

  return (
    <div className={styles.panel}>
      <div className={styles.builder}>
        <div className={styles.sides}>
          {sides.map((s, i) => (
            <span key={s.ref} className={`${styles.sideChip} ${s.kind === 'document' ? styles.sideDoc : ''}`}>
              <span className={styles.sideIndex}>{i + 1}</span>{s.label}
              <button type="button" aria-label={`Remove ${s.label}`} onClick={() => setSides(sides.filter((x) => x.ref !== s.ref))}>×</button>
            </span>
          ))}
          {sides.length === 0 && <span className={styles.hint}>Add two to five regulations or documents.</span>}
        </div>

        <div className={styles.pickers}>
          <div className={styles.picker}>
            <label className={styles.label}>Add a regulation</label>
            <input className={styles.input} placeholder="Search by title…" value={query} onChange={(e) => setQuery(e.target.value)} />
            {(candidates.length > 0 || searching) && (
              <ul className={styles.candidates}>
                {searching && candidates.length === 0 && <li className={styles.hint}>Searching…</li>}
                {candidates.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => addSide({ ref: String(c.id), label: c.title, kind: 'regulation' })}>
                      <span>{c.title}</span><small>{c.jurisdiction || c.jurisdiction_code || ''}</small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className={styles.picker}>
            <label className={styles.label}>Add one of my documents</label>
            <select className={styles.input} value="" onChange={(e) => {
              const d = documents.find((x) => String(x.id) === e.target.value);
              if (d) addSide({ ref: documentRef(d.id), label: d.document_name, kind: 'document' });
            }}>
              <option value="">{documents.length ? 'Choose a document…' : 'No documents uploaded yet'}</option>
              {documents.map((d) => <option key={d.id} value={d.id}>{d.document_name}</option>)}
            </select>
            <span className={styles.hint}>Upload documents in the <Link to="/grc/repository">repository</Link>.</span>
          </div>
        </div>

        {error && <p className={styles.error}>{error}</p>}
        <button type="button" className={styles.run} onClick={run} disabled={running || sides.length < 2}>
          {running ? 'Building matrix…' : `Build matrix (${sides.length} side${sides.length === 1 ? '' : 's'})`}
        </button>
      </div>

      {result && (
        <div className={styles.result}>
          <div className={styles.summaryRow}>
            <div>
              <div className={styles.eyebrow}>Completeness</div>
              <div className={styles.big}>{result.completeness.complete_rows}/{result.completeness.rows} requirements addressed by every side</div>
            </div>
            {result.sides.map((s) => {
              const ps = result.completeness.per_side[s.side_index];
              return (
                <div key={s.side_index} className={styles.sideStat}>
                  <div className={styles.eyebrow}>{s.side_index + 1} · {s.kind === 'document' ? 'Your document' : s.jurisdiction || 'Regulation'}</div>
                  <div>{sideTitle(s.side_index)}</div>
                  <small>{ps.covered} covered · {ps.partial} partial · {ps.absent} absent{!s.extracted ? ' · no text available' : ''}</small>
                </div>
              );
            })}
          </div>

          {result.executive_summary && <p className={styles.summary}>{result.executive_summary}</p>}

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Requirement</th>
                  {result.sides.map((s) => <th key={s.side_index}>{s.side_index + 1} · {sideTitle(s.side_index)}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row) => (
                  <tr key={row.category_id} className={row.complete ? '' : styles.incomplete}>
                    <th scope="row">
                      {row.category}
                      {!row.is_standard && <span className={styles.tag}>policy-specific</span>}
                    </th>
                    {row.cells.map((cell) => {
                      const key = `${row.category_id}:${cell.side_index}`;
                      const open = openCell === key;
                      return (
                        <td key={key} className={styles[cell.coverage]}>
                          <button type="button" className={styles.cellBtn} onClick={() => setOpenCell(open ? null : key)} disabled={cell.coverage === 'absent'}>
                            <span className={styles.mark}>{COVERAGE_LABEL[cell.coverage]}</span>
                            {cell.coverage !== 'absent' && <small>{cell.points.length} point{cell.points.length === 1 ? '' : 's'}</small>}
                          </button>
                          {open && (
                            <ul className={styles.points}>
                              {cell.points.map((p, i) => <li key={i}>{p}</li>)}
                            </ul>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(result.gaps.length > 0 || result.recommendations.length > 0 || result.analysis_note) && (
            <div className={styles.analysis}>
              <div>
                <div className={styles.eyebrow}>Gaps</div>
                {result.analysis_note && <p className={styles.hint}>{result.analysis_note}</p>}
                {result.gaps.length === 0 && !result.analysis_note && <p className={styles.hint}>No gaps identified.</p>}
                <ul className={styles.list}>
                  {result.gaps.map((g, i) => (
                    <li key={i} className={styles[`sev_${g.severity}`]}>
                      <strong>{sideTitle(g.side_index)}</strong> · {rowsById.get(g.category_id)?.category || g.category_id}
                      <span className={styles.sev}>{g.severity}</span>
                      <div>{g.description}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className={styles.eyebrow}>Recommendations</div>
                <ol className={styles.list}>
                  {result.recommendations.map((r, i) => (
                    <li key={i}>
                      <strong>{r.action}</strong>
                      {r.side_index !== null && r.side_index !== undefined && <span className={styles.tag}>{sideTitle(r.side_index)}</span>}
                      {r.rationale && <div>{r.rationale}</div>}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatrixPanel;
